import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Breadcrumb, Icon, PageTitle, Pagination, Template } from '@components'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import SearchInput from '@components/SearchInput'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Tr from '@components/Tr'
import { successMessages } from '@constants/feedbackMessages'
import { PencilAltIcon, TrashIcon } from '@heroicons/react/outline'
import { userApi } from '@services/UserApi'
import { loadingModal, openAlertModal, openModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import { useFilters, useInputBounce, useQuery } from '@utils/hooks'

import AddUser from './AddUser'
import EditUser from './EditUser'

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] mt-[32px] items-end flex-wrap',
	tag: 'font-medium text-blue-600 underline underline-offset-2',
	divider: 'mt-[32px] mb-[12px]',
	avatar: 'rounded-full max-w-[40px] max-h-[40px] min-w-[40px] min-h-[40px]',
}

function ListUsers() {
	const pages = [{ name: 'Usuários', href: '#', current: true }]
	const dispatch = useAppDispatch()
	const query = useQuery()
	const navigate = useNavigate()

	const pageQuery = Number(query.get('page')) || 1
	const searchQs = query.get('search') || ''

	const [search, setSearch] = useState(decodeURIComponent(searchQs))
	const [searchQuery, setSearchQuery] = useState(decodeURIComponent(searchQs))
	const [page, setPage] = useState(pageQuery)

	const { data, isLoading } = userApi.useListQuery({
		page,
		search: encodeURIComponent(searchQuery),
	})

	const searchCallback = useCallback(
		(value: string) => {
			navigate('/usuarios?page=1')
			setSearchQuery(value)
		},
		[navigate]
	)

	const [deleteUser, { isLoading: loadingDelete, isSuccess: deleteSuccess }] =
		userApi.useDeleteMutation()

	useEffect(() => {
		dispatch(loadingModal({ loading: loadingDelete }))

		if (deleteSuccess) {
			dispatch(notificationCreators.show(successMessages.deleteUser))
		}
	}, [dispatch, loadingDelete, deleteSuccess])

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	useInputBounce(search, searchCallback)

	useFilters({
		search: searchQuery,
		fallbackUrl: '/usuarios',
	})

	return (
		<>
			<Template>
				<Breadcrumb pages={pages} />
				<PageTitle title='Usuários' />
				<Flex className={styles.searchContainer}>
					<SearchInput
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className='flex-1'
						placeholder='Buscar Usuário'
					/>

					<Button onClick={() => dispatch(openModal({ name: 'addUser' }))}>
						Adicionar Usuário
					</Button>
				</Flex>

				{isLoading ? (
					<div className='flex justify-center'>
						<Spinner size='md' />
					</div>
				) : (
					<Table
						th={[
							{
								label: 'nome',
							},
							{ label: 'e-mail' },
							{ label: 'ações' },
						]}
					>
						{data?.data && data?.data?.length > 0 ? (
							data?.data.map(({ id: userId, name, email, imageUrl }) => (
								<Tr key={userId}>
									<Td className='w-[60%] px-6'>
										<Link to={`/usuarios/${userId}`}>
											<Flex className='items-center gap-4'>
												{imageUrl ? (
													<img
														src={imageUrl}
														alt={name}
														className=' h-11 w-11 rounded-full'
													/>
												) : (
													<Icon
														name='avatar-default'
														className='w-11 h-11 rounded-full max-w-[40px] max-h-[40px] min-w-[40px] min-h-[40px]'
													/>
												)}
												{name}
											</Flex>
										</Link>
									</Td>
									<Td>
										<Link to={`/usuarios/${userId}`}>
											<Flex className='items-center gap-4'>{email}</Flex>
										</Link>
									</Td>

									<Td>
										<Flex className='items-center gap-4'>
											<Button
												className='!px-2 !py-2'
												leftIcon={<PencilAltIcon />}
												disabled={false}
												variant='outline'
												onClick={() =>
													dispatch(
														openModal({
															name: 'editUser',
															modalInfo: userId,
														})
													)
												}
											>
												<></>
											</Button>
											<Button
												className='!px-2 !py-2'
												leftIcon={<TrashIcon />}
												disabled={false}
												variant='outline'
												onClick={() => {
													const removeUser = {
														title: 'Remover Usuário',
														message:
															'Tem certeza que deseja remover o usuário?',
														buttonLabel: 'Remover Usuário',
														onClick: () =>
															deleteUser({
																id: userId,
															}),
													}

													dispatch(openAlertModal(removeUser))
												}}
											>
												<></>
											</Button>
										</Flex>
									</Td>
								</Tr>
							))
						) : (
							<EmptyRow tdAmount={3} message='Nenhum usuário encontrado' />
						)}
					</Table>
				)}

				<Divider className={styles.divider} />
				{data?.data && data?.data?.length > 0 && (
					<Pagination
						pageCount={data?.meta.lastPage || 0}
						maxAmount={data?.meta.total || 0}
						perPage={data?.meta.perPage || 0}
					/>
				)}
			</Template>
			<AddUser />
			<EditUser />
		</>
	)
}

export default ListUsers
